import { useMemo } from 'react';

import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { SlideshowOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { usePreferences } from '@/react-query/usePreferences';

import { selectedVisitAtom } from '../../../app/appHeader/imageViewerMenu/atoms';
import { blueBlack400, green400 } from '../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { selectQuadrantsAtom, selectTeethAtom } from '../../patientList/atoms';
import { imageBrowserExpandedAtom } from '../multiFileViewer/findingViewer/imageBrowser/atoms';
import { useGetFilteredImages } from '../multiFileViewer/findingViewer/imageBrowser/helpers';
import { showSegmentationAtom } from '../multiFileViewer/imageToolbar/atoms';
import type { BoundingBoxFinding } from '../types';

import {
  casePresentationModeAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
} from './atoms';

const PresentButton = styled(Button)`
  box-sizing: border-box;
  display: flex;
  text-transform: none;
  color: white;
  background-color: ${green400};
  font-size: 0.75em;
  font-weight: bold;
  text-align: left;
  line-height: 1.1em;
  padding: 0.5em 1em;
  margin-top: 0.5em;
  gap: 1em;
  &:hover {
    background-color: ${blueBlack400};
  }
`;

export const PresentSingleFinding = ({
  finding,
  tooth,
  onClick,
}: {
  finding: BoundingBoxFinding;
  tooth?: number;
  onClick?: () => void;
}) => {
  const setCasePresentationMode = useSetAtom(casePresentationModeAtom);
  const setShowSegmentation = useSetAtom(showSegmentationAtom);
  const setSelectedFindingBounds = useSetAtom(selectedFindingsAtom);
  const setBrowserExpanded = useSetAtom(imageBrowserExpandedAtom);
  const setSelectedFindingImageId = useSetAtom(selectedFindingImageIdAtom);

  const estimatedVisitId = useAtomValue(selectedVisitAtom);
  const { trackEvent } = useTrackEvent();
  const images = useGetFilteredImages({
    ifCasePresentationMode: true,
    ifPresentSingleFindings: true,
  });
  const { id: patientId, imageId: currentImage } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const { data: prefs } = usePreferences();

  if (prefs?.useSidebarRedesign) {
    return null;
  }

  return (
    <PresentButton
      onClick={() => {
        if (onClick) {
          onClick();
        }
        setSelectedFindingImageId(currentImage);
        setCasePresentationMode(true);
        setShowSegmentation(true);
        setSelectedFindingBounds({
          id: finding.id,
          xMin: finding.xMin,
          yMin: finding.yMin,
          xMax: finding.xMax,
          yMax: finding.yMax,
          x: finding.x,
          y: finding.y,
          type: finding.rblType ? 'rbl' : finding.type,
          tooth: finding.rblType ? tooth : finding.toothNumber,
        });
        setBrowserExpanded(false);
        trackEvent(mixpanelEvents.casePresentationView, {
          findingIds: [finding.id],
          findingType: finding.rblType ? 'rbl' : finding.type,
          source: 'hovered finding',
          estimatedVisitId: estimatedVisitId,
          images: images?.map((image) => image.imageId),
          patientId,
        });
      }}
      startIcon={<SlideshowOutlined />}
      
    >
      <Trans>Present Finding</Trans>
    </PresentButton>
  );
};

export const PresentAllFindings = ({ onClick }: { onClick?: () => void }) => {
  const setCasePresentationMode = useSetAtom(casePresentationModeAtom);
  const setShowSegmentation = useSetAtom(showSegmentationAtom);
  const setBrowserExpanded = useSetAtom(imageBrowserExpandedAtom);

  const estimatedVisitId = useAtomValue(selectedVisitAtom);
  const { trackEvent } = useTrackEvent();
  const images = useGetFilteredImages({ ifCasePresentationMode: true });
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const selectedQuadrants = useAtomValue(selectQuadrantsAtom);
  const { id, imageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const { t } = useLingui();
  const { data: prefs } = usePreferences();

  const selectedImage = images?.find((image) => image.imageId === imageId);

  const buttonSource = useMemo(() => {
    let source = 'present all findings';
    if (selectedQuadrants.length) source = 'presenting quadrant';
    else if (selectedTeeth.length) source = 'presenting tooth';
    return source;
  }, [selectedQuadrants.length, selectedTeeth.length]);

  const selectedTeethLabel = `${t`For`} ${
    selectedTeeth.length > 1 ? t`Teeth` : t`Tooth`
  }`;

  if (prefs?.useSidebarRedesign) {
    return null;
  }

  return (
    <PresentButton
      
      onClick={() => {
        if (onClick) {
          onClick();
        }
        setCasePresentationMode(true);
        setShowSegmentation(true);
        setBrowserExpanded(false);
        trackEvent(mixpanelEvents.casePresentationView, {
          source: buttonSource,
          estimatedVisitId: estimatedVisitId,
          images: images?.map((image) => image.imageId),
          teeth: selectedTeeth,
          selectedQuadrants,
          patientId: id,
          originView: imageId ? 'SINGLE_FINDING_VIEW' : 'FMX_VIEW',
          imageType: selectedImage
            ? selectedImage?.detectedImageType === 'COLOR_IMAGE'
              ? 'DENTAL_PHOTO'
              : 'X-RAY'
            : undefined,
        });
      }}
      startIcon={<SlideshowOutlined />}
    >
      <Trans>Present Findings</Trans>{' '}
      {!!selectedQuadrants.length && <Trans>For Quadrant</Trans>}
      {!selectedQuadrants.length &&
        !!selectedTeeth.length &&
        selectedTeethLabel}
    </PresentButton>
  );
};
